import React, { useContext } from "react";

import { Flex, Price, LegacyText, Spinner } from "flicket-ui";
import orderBy from "lodash/orderBy";

import { LineItemType, OrderQuery } from "~graphql/sdk";
import { formatSeatLocation, formatTicketName } from "~lib/helpers";
import { BaseContext } from "~context";

type LineItem = OrderQuery["order"]["lineItems"]["edges"][number]["node"];

type ListProps = {
  lineItems: LineItem[];
  hidePricing?: boolean;
  activePromotions: any[];
  isNonSeated: boolean;
  isComp?: boolean;
};

export const getTicketTypeName = (
  type: LineItem["type"],
  lineItemName: string,
  membershipType?: LineItem["membershipType"],
  ticketType?: LineItem["ticketType"],
  pricingTierName?: string | null
) => {
  if (type === LineItemType.Addon) {
    return lineItemName;
  }

  if (type === LineItemType.Point) {
    return `${lineItemName} package`;
  }

  const ticketName = membershipType?.membership
    ? lineItemName.split(":")[1]
    : formatTicketName(ticketType?.name, pricingTierName);

  return ticketName;
};

const ListItem = ({
  id,
  price,
  membershipType,
  ticketType,
  seatLabel,
  seatZone,
  seatSection,
  hidePricing,
  originalPrice,
  type,
  name,
  quantity,
  pointItem,
  isMultiBuy,
  isNonSeated,
  multiBuy,
  isComp = false,
  pricingTierName,
}: LineItem & {
  isMultiBuy: boolean;
  multiBuy: any;
  hidePricing: boolean;
  isNonSeated: boolean;
  isComp?: boolean;
}) => {
  const { organization } = useContext(BaseContext);

  let multiBuyQty = 1;
  let multiBuyItemPrice = originalPrice;
  let multiBuySavings = 0;

  if (multiBuy) {
    multiBuyQty = multiBuy.giftedQty ?? 1;
    multiBuySavings = originalPrice * multiBuyQty;

    if (multiBuy.promotion.price > 0) {
      multiBuyItemPrice = Math.min(
        originalPrice,
        multiBuy.promotion.price as number
      );

      multiBuySavings =
        (originalPrice - multiBuy.promotion.price) * multiBuyQty;

      if (multiBuy.promotion.price > originalPrice) {
        isMultiBuy = false;
      }
    }
  }

  return (
    <Flex
      w="100%"
      justifyContent="space-between"
      mb={3}
      lineHeight="normal"
      key={id}
    >
      <Flex flexDir="column">
        <LegacyText
          fontSize={3}
          lineHeight="normal"
          fontWeight={hidePricing ? "extraBold" : "demiBold"}
          color="N800"
          textTransform="capitalize"
          mb={1}
        >
          {getTicketTypeName(
            type,
            name,
            membershipType,
            ticketType,
            pricingTierName
          )}
        </LegacyText>

        <LegacyText color="N600" fontSize={1} lineHeight="normal">
          {type === LineItemType.Addon || isNonSeated ? (
            <>
              {quantity}x{" "}
              <Price
                color="N800"
                fontSize={3}
                lineHeight="normal"
                price={originalPrice}
                currency={organization.currency}
              />
              <LegacyText color="N600" fontSize={1} mt="1/2">
                {type === LineItemType.Point &&
                  `${quantity * pointItem?.amount} ${organization.point?.name}`}
              </LegacyText>
            </>
          ) : (
            formatSeatLocation(seatZone, seatSection, seatLabel)
          )}
        </LegacyText>
        {isMultiBuy && (
          <LegacyText
            fontWeight="demiBold"
            fontSize={1}
            lineHeight="normal"
            color="N600"
            mt={"1/2"}
          >
            Multibuy promotion applied, you saved{" "}
            <Price price={multiBuySavings} currency={organization.currency} />
          </LegacyText>
        )}
      </Flex>
      {!hidePricing && (
        <>
          {isMultiBuy ? (
            multiBuyItemPrice * multiBuyQty === originalPrice * quantity ? (
              <LegacyText color="N800" fontSize={3} lineHeight="normal">
                Free
              </LegacyText>
            ) : (
              <Flex flexDirection="column" alignItems="flex-end">
                <Price
                  color="N800"
                  fontSize={3}
                  lineHeight="normal"
                  price={originalPrice * quantity}
                  currency={organization.currency}
                  strikeThrough={isComp}
                />
                <Price
                  mt={1}
                  fontSize={1}
                  lineHeight="normal"
                  price={-multiBuySavings}
                  color="success"
                  currency={organization.currency}
                />
              </Flex>
            )
          ) : (
            <Flex flexDirection="column" alignItems="flex-end">
              <Price
                color="N800"
                fontSize={3}
                lineHeight="normal"
                price={originalPrice * quantity}
                currency={organization.currency}
                strikeThrough={isComp}
              />
              {price !== originalPrice && (
                <Price
                  mt={1}
                  fontSize={1}
                  lineHeight="normal"
                  price={-((originalPrice - price) * quantity)}
                  currency={organization.currency}
                  color="success"
                />
              )}
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};

export const List = ({
  lineItems,
  activePromotions = [],
  hidePricing = false,
  isNonSeated = false,
  isComp = false,
}: ListProps) => (
  <>
    {lineItems?.length ? (
      orderBy(lineItems, ["type", "name"], ["desc", "asc"]).map((lineItem) => {
        const isMultiBuy = activePromotions.find(
          (promo) =>
            promo.id === lineItem.id ||
            (lineItem?.ticketType?.id || lineItem?.membershipType?.id) ===
              promo?.getType?.id
        );

        return (
          <ListItem
            {...lineItem}
            key={lineItem.id}
            hidePricing={hidePricing}
            isMultiBuy={
              isMultiBuy &&
              (isMultiBuy?.type === "getting" || isMultiBuy?.giftedQty > 0)
            }
            multiBuy={isMultiBuy}
            isNonSeated={isNonSeated}
            isComp={isComp}
          />
        );
      })
    ) : (
      <Spinner />
    )}
  </>
);
